import { differenceInDays, parseISO } from "date-fns";

import { PaywallFeatureStatus } from "services/types/paywall.type";

export interface TrialInfo {
  isInTrial: boolean;
  daysRemaining: number;
  trialEndDate: string | null;
  status: PaywallFeatureStatus["status"];
}

export function getTrialInfo(featureStatus?: PaywallFeatureStatus): TrialInfo {
  const defaultInfo: TrialInfo = {
    isInTrial: false,
    daysRemaining: 0,
    trialEndDate: null,
    status: "active"
  };

  if (!featureStatus) {
    return defaultInfo;
  }

  const { is_in_trial, trial_date, status } = featureStatus;
  const now = new Date();

  // A trial that has ended is no trial: a store read before the end still says `is_in_trial`, and
  // the formula below would keep a banner up for up to two days after it (SN-19868).
  if (!is_in_trial || !trial_date || parseISO(trial_date) <= now) {
    return {
      ...defaultInfo,
      status
    };
  }

  const trialEndDate = parseISO(trial_date);
  const daysRemaining = Math.max(0, differenceInDays(trialEndDate, now) + 1);

  return {
    isInTrial: is_in_trial,
    daysRemaining,
    trialEndDate: trial_date,
    status
  };
}

export function getTrialStatusMessage(trialInfo: TrialInfo): string {
  if (!trialInfo.isInTrial) {
    return "";
  }

  const { daysRemaining, status } = trialInfo;

  if (status === "trialing") {
    if (daysRemaining === 0) {
      return "Your trial expires today";
    } else if (daysRemaining === 1) {
      return "1 day remaining in your trial";
    } else {
      return `${daysRemaining} days remaining in your trial`;
    }
  }

  return "";
}

export function shouldShowTrialBanner(
  featureStatus?: PaywallFeatureStatus
): boolean {
  if (!featureStatus) {
    return false;
  }

  const trialInfo = getTrialInfo(featureStatus);
  return trialInfo.isInTrial && trialInfo.status === "trialing";
}
